import React, { createContext, useState, useContext, useEffect, useCallback, useRef, useMemo } from 'react';
import axios from 'axios';

import { API_URL } from '../utils/apiUrl';

const AuthContext = createContext();

const INACTIVITY_TIMEOUT_MS = 5 * 60 * 1000; // 5 minutes

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  // Initialize user from localStorage immediately to prevent flash
  const [user, setUser] = useState(() => {
    try {
      // Regular JWT user
      const storedUser = localStorage.getItem('user');
      if (storedUser) return JSON.parse(storedUser);
      // Legacy florist token (separate auth system)
      const floristToken = localStorage.getItem('florist_token');
      if (floristToken) {
        return {
          tipo: 'fiorista',
          id: localStorage.getItem('florist_id') || '',
          nome: localStorage.getItem('florist_name') || 'Negozio',
        };
      }
      return null;
    } catch {
      return null;
    }
  });
  const [token, setToken] = useState(localStorage.getItem('token') || localStorage.getItem('florist_token'));
  const [loading, setLoading] = useState(true);

  const [subscriptionBlocked, setSubscriptionBlocked] = useState(false);
  const [subscriptionBlockReason, setSubscriptionBlockReason] = useState(null);

  
  // Check subscription access for business users
  const checkSubscriptionAccess = useCallback(async () => {
    if (!token || !user) return;
    
    // Skip for privato, admin, and florist (florist has its own /api/fioristi/dashboard/plan endpoint)
    if (['privato', 'admin', 'fiorista'].includes(user?.tipo)) {
      setSubscriptionBlocked(false);
      return;
    }
    
    try {
      const response = await axios.post(`${API_URL}/api/subscriptions/check-access`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data.blocked) {
        setSubscriptionBlocked(true);
        setSubscriptionBlockReason(response.data);
      } else {
        setSubscriptionBlocked(false);
        setSubscriptionBlockReason(null);
      }
    } catch (error) {
      console.error('Error checking subscription:', error);
    }
  }, [token, user]);

  // Setup axios interceptor for auth token - SYNCHRONOUS on initial render
  useEffect(() => {
    if (token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    } else {
      delete axios.defaults.headers.common['Authorization'];
    }
   
  }, [token]);

  // Load/verify user on mount if token exists
  useEffect(() => {
    const loadUser = async () => {
      const storedToken = localStorage.getItem('token');
      if (storedToken) {
        // Set header BEFORE making the request
        axios.defaults.headers.common['Authorization'] = `Bearer ${storedToken}`;
        try {
          const response = await axios.get(`${API_URL}/api/auth/me`);
          setUser(response.data);
          // Update localStorage with fresh user data
          localStorage.setItem('user', JSON.stringify(response.data));
        } catch (error) {
          console.error('Failed to load user:', error);
          // Token might be invalid, clear it
          logout();
        }
      } else {
        // Check florist token (legacy auth - no /me endpoint)
        const floristToken = localStorage.getItem('florist_token');
        if (floristToken) {
          // Verify token by fetching florist profile (don't set global axios Authorization
          // since regular API calls may not accept florist tokens)
          try {
            const r = await axios.get(`${API_URL}/api/fioristi/dashboard/profilo`, {
              headers: { Authorization: `Bearer ${floristToken}` }
            });
            setToken(floristToken);
            setUser({
              tipo: 'fiorista',
              id: r.data?.id || localStorage.getItem('florist_id') || '',
              nome: r.data?.nome_negozio || localStorage.getItem('florist_name') || 'Negozio',
              email: r.data?.email,
            });
          } catch {
            // Invalid florist token, clear it
            localStorage.removeItem('florist_token');
            localStorage.removeItem('florist_id');
            localStorage.removeItem('florist_name');
            setUser(null);
            setToken(null);
          }
        }
      }
      setLoading(false);
    };

    loadUser();
   
  }, []);

  // Check subscription when user changes
  useEffect(() => {
    if (user && token) {
      checkSubscriptionAccess();
    }
   
  }, [user, token]);

  const login = useCallback(async (email, password) => {
    try {
      const response = await axios.post(`${API_URL}/api/auth/login`, {
        email,
        password
      });
      
      const { access_token, user: userData, needs_contract_reacceptance, contract_reacceptance_info } = response.data;
      
      setToken(access_token);
      setUser(userData);
      localStorage.setItem('token', access_token);
      localStorage.setItem('user', JSON.stringify(userData));
      
      // Check if user needs to re-accept contracts
      if (needs_contract_reacceptance) {
        return { 
          success: true, 
          needs_contract_reacceptance: true,
          contract_reacceptance_info,
          user: userData
        };
      }
      
      return { success: true, user: userData };
    } catch (error) {
      const detail = error.response?.data?.detail;
      let errorMessage = 'Errore durante il login';
      if (typeof detail === 'string') {
        errorMessage = detail;
      } else if (Array.isArray(detail) && detail.length > 0) {
        errorMessage = detail.map(e => e.msg || e.message || JSON.stringify(e)).join(', ');
      }
      return {
        success: false,
        error: errorMessage
      };
    }
  }, []);

  const register = useCallback(async (userData) => {
    try {
      const response = await axios.post(`${API_URL}/api/auth/register`, userData);
      
      const { access_token, user: newUser } = response.data;
      
      setToken(access_token);
      setUser(newUser);
      localStorage.setItem('token', access_token);
      localStorage.setItem('user', JSON.stringify(newUser));
      
      return { success: true };
    } catch (error) {
      const detail = error.response?.data?.detail;
      let errorMessage = 'Errore durante la registrazione';
      if (typeof detail === 'string') {
        errorMessage = detail;
      } else if (Array.isArray(detail) && detail.length > 0) {
        errorMessage = detail.map(e => e.msg || e.message || JSON.stringify(e)).join(', ');
      }
      return {
        success: false,
        error: errorMessage
      };
    }
  }, []);

  const logout = useCallback(() => {
    setToken(null);
    setUser(null);
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    // Also clear florist legacy session
    localStorage.removeItem('florist_token');
    localStorage.removeItem('florist_id');
    localStorage.removeItem('florist_name');
    delete axios.defaults.headers.common['Authorization'];
    // Clear httpOnly cookie via backend
    axios.post(`${API_URL}/api/auth/logout`).catch(() => {});
   
  }, []);

  // Auto-logout after inactivity (escluso super admin)
  const inactivityTimer = useRef(null);
  const isLoggedIn = useRef(false);
  const currentUser = useRef(null);

  const resetInactivityTimer = useCallback(() => {
    if (!isLoggedIn.current) return;
    // PAUSE auto-logout for super admin (richiesto dall'utente)
    if (currentUser.current?.admin_level === 'super') return;
    if (inactivityTimer.current) clearTimeout(inactivityTimer.current);
    inactivityTimer.current = setTimeout(() => {
      if (isLoggedIn.current) {
        logout();
        window.location.href = '/login?reason=inactivity';
      }
    }, INACTIVITY_TIMEOUT_MS);
   
  }, [logout]);

  useEffect(() => {
    isLoggedIn.current = !!token && !!user;
    currentUser.current = user;
    // Super admin: skip the inactivity timer entirely (richiesto dall'utente)
    if (isLoggedIn.current && user?.admin_level !== 'super') {
      resetInactivityTimer();
      const events = ['mousedown', 'keydown', 'scroll', 'touchstart', 'mousemove'];
      events.forEach(event => window.addEventListener(event, resetInactivityTimer, { passive: true }));
      return () => {
        events.forEach(event => window.removeEventListener(event, resetInactivityTimer));
        if (inactivityTimer.current) clearTimeout(inactivityTimer.current);
      };
    } else {
      if (inactivityTimer.current) clearTimeout(inactivityTimer.current);
    }
   
  }, [token, user, resetInactivityTimer]);

  // Direct login with token and user data (used by admin login and florist login)
  const setAuthData = useCallback((accessToken, userData) => {
    setToken(accessToken);
    setUser(userData);
    // Florist tokens live in 'florist_token' (separate auth system).
    // Do NOT clobber localStorage['token'] – that triggers /api/auth/me 401 on reload.
    if (userData?.tipo === 'fiorista') {
      localStorage.setItem('florist_token', accessToken);
      if (userData.id) localStorage.setItem('florist_id', userData.id);
      if (userData.nome) localStorage.setItem('florist_name', userData.nome);
    } else {
      localStorage.setItem('token', accessToken);
      localStorage.setItem('user', JSON.stringify(userData));
    }
    axios.defaults.headers.common['Authorization'] = `Bearer ${accessToken}`;
  }, []);

  const updateProfile = useCallback(async (profileData) => {
    try {
      const response = await axios.put(`${API_URL}/api/auth/profile`, profileData);
      setUser(response.data);
      localStorage.setItem('user', JSON.stringify(response.data));
      return { success: true };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.detail || 'Errore durante l\'aggiornamento'
      };
    }
  }, []);

  const value = useMemo(() => ({
    user,
    token,
    loading,
    login,
    register,
    logout,
    setAuthData,
    updateProfile,
    checkSubscriptionAccess,
    subscriptionBlocked,
    subscriptionBlockReason,
    isAuthenticated: !!user,
    isPrivato: user?.tipo === 'privato',
    isAgenzia: user?.tipo === 'agenzia',
    isAdmin: user?.tipo === 'admin',
    isFiorista: user?.tipo === 'fiorista',
    isProfessionista: user?.tipo === 'professionista',
    isBlogger: user?.tipo === 'blogger'
  }), [user, token, loading, login, register, logout, setAuthData, updateProfile, checkSubscriptionAccess, subscriptionBlocked, subscriptionBlockReason]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
